<template>
  <details class="completion-accordion" :open="isOpen" @toggle="onToggle">
    <summary class="completion-accordion__summary">
      <span class="completion-accordion__title">{{ title }}</span>
      <slot name="badge"></slot>
      <span class="completion-accordion__chevron" aria-hidden="true">
        <span class="iconfont iarrow-down"></span>
      </span>
    </summary>
    <template v-if="isOpen">
      <div v-if="showDivider" class="completion-accordion__divider"></div>
      <div class="completion-accordion__content">
        <slot></slot>
      </div>
    </template>
  </details>
</template>

<script setup lang="ts">
import { ref } from 'vue'

const props = withDefaults(
  defineProps<{
    title: string
    defaultOpen?: boolean
    showDivider?: boolean
  }>(),
  {
    defaultOpen: false,
    showDivider: true,
  },
)

const isOpen = ref(props.defaultOpen)

function onToggle(event: Event): void {
  isOpen.value = (event.currentTarget as HTMLDetailsElement).open
}
</script>

<style lang="scss" scoped>
.completion-accordion {
  border: 1px solid $c-black-13;
  border-radius: 14px;
  background: $c-white;
  transition: border-color 0.15s;

  &:not([open]) {
    @include hover {
      border-color: $c-border-8;
    }
  }

  &[open] {
    border-color: $c-black-3;
  }

  &__summary {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 32px;
    padding: 16px;
    color: $c-black-2;
    font-size: 18px;
    line-height: 24px;
    cursor: pointer;
    list-style: none;

    &::-webkit-details-marker {
      display: none;
    }
  }

  &__title {
    flex: 1;
    font-weight: 500;
  }

  &__chevron {
    display: grid;
    flex: 0 0 32px;
    width: 32px;
    height: 32px;
    place-items: center;
    font-size: 24px;
  }

  &[open] &__chevron {
    transform: rotate(180deg);
  }

  &[open] &__summary {
    padding-bottom: 12px;
  }

  &__divider {
    height: 1px;
    margin: 0 16px 12px;
    background: $c-border-7;
  }

  &__content {
    padding: 0 16px 16px;
  }
}

.night-mode .completion-accordion {
  border: 0;
  border-radius: 12px;
  background: $c-dark-2;
  transition: background-color 0.15s;

  &:not([open]) {
    @include hover {
      background: $c-dark-3;
    }
  }

  &__summary {
    color: $c-dark-4;
  }

  &__divider {
    background: $c-dark-3;
  }
}
</style>
